import noop from 'lodash/noop';
import { onMount } from 'svelte';
import * as z from 'zod';
import {
    type PublicMessage_CompanyOnline,
    type PublicMessage_UserOnline,
    type UserEventDataByEventName,
    zUserEventData,
} from '$lib/mercure/MercureMessage';
import singleton from '$lib/utils/factory';
import * as Result from '$lib/utils/result';
import type { lazy } from '$lib/utils/types';
import { type JsonObject, safeParse, safeParseJsonObject } from '$lib/utils/zod';
import type { ParticipantInput } from '$gql/graphql';
import { matchParticipantInput } from '$gql/resolvers/ParticipantRef';
import type { CurrentUserStore } from '$components/CurrentUserStore/CurrentUserStore';

type callback = (event: JsonObject) => void;

export class InSvelte {
    constructor(private readonly manager: MercureManager) {}

    onPrivateEvent<EventName extends keyof UserEventDataByEventName>(
        this: this,
        eventName: EventName,
        callback: (event: UserEventDataByEventName[EventName]) => void,
    ) {
        onMount(() => {
            try {
                return this.manager.onPrivateEvent(eventName, callback);
            } catch {
                /* empty */
            }
        });
    }

    onPublicParticipantOnline(
        this: this,
        participant: ParticipantInput,
        callback: (participant: ParticipantInput, online: boolean) => void,
    ) {
        onMount(() => {
            try {
                return this.manager.onPublicParticipantOnline(participant, callback);
            } catch {
                /* empty */
            }
        });
    }
}

export class MercureManager {
    private topicESMap: Record<string, EventSource> = {};
    private topicListenersMap: Record<string, callback[]> = {};

    constructor(
        private readonly mercureUrl: string,
        private readonly browser: boolean,
        private readonly currentUserStore: lazy<CurrentUserStore>,
    ) {
        this.topicESMap = {};
        this.topicListenersMap = {};
    }

    initTopicEs(this: this, topic: string) {
        if (!this.browser) {
            return;
        }

        if (this.topicESMap[topic]) {
            return this.topicESMap[topic];
        }

        const url = new URL(this.mercureUrl);
        url.searchParams.append('topic', topic);

        let eventSource = new EventSource(url, {
            withCredentials: true,
        });

        this.topicESMap[topic] = eventSource;

        eventSource.onmessage = (e: MessageEvent) => {
            const data = safeParseJsonObject(e.data as string);
            if (Result.isFailure(data)) {
                return;
            }

            console.debug('mercure message from ', topic, data);
            let listeners = this.topicListenersMap[topic] ?? [];
            for (let listener of listeners) {
                listener(data.data);
            }
        };

        eventSource.onopen = (e) => {
            console.debug('open for topic', topic, e);
        };

        eventSource.onerror = (e) => {
            console.error('error for topic', topic, e);
        };

        return eventSource;
    }

    onPrivateEvent<EventName extends keyof UserEventDataByEventName>(
        this: this,
        eventName: EventName,
        callback: (event: UserEventDataByEventName[EventName]) => void,
    ): () => void {
        let cleanOnTopic = noop;
        return this.currentUserStore()
            .currentUserId()
            .subscribe(
                (userId) => {
                    cleanOnTopic = this.onTopic({
                        topic: `/users/${userId}`,
                        callback: (event: JsonObject) => {
                            const data = safeParse(zUserEventData, event);
                            if (Result.isOk(data) && data.data.event === eventName) {
                                callback(
                                    data.data as UserEventDataByEventName[EventName],
                                );
                            }
                        },
                    });
                },
                () => {
                    cleanOnTopic();
                    cleanOnTopic = noop;
                },
            );
    }

    onPublicUserOnline(
        this: this,
        userId: number,
        callback: (event: PublicMessage_UserOnline['payload']) => void,
    ) {
        return this.onTopic({
            topic: `/public/user/${userId}/online`,
            callback: (event: JsonObject) => {
                const data = safeParse(
                    z.object({
                        payload: z.object({
                            id: z.number(),
                            online: z.boolean(),
                            eventId: z.string(),
                        }),
                    }),
                    event,
                );

                if (Result.isOk(data)) {
                    callback(data.data.payload);
                }
            },
        });
    }

    onPublicCompanyOnline(
        this: this,
        companyId: number,
        callback: (event: PublicMessage_CompanyOnline['payload']) => void,
    ) {
        return this.onTopic({
            topic: `/public/company/${companyId}/online`,
            callback: (event: JsonObject) => {
                const data = safeParse(
                    z.object({
                        payload: z.object({
                            id: z.number(),
                            online: z.boolean(),
                            eventId: z.string(),
                        }),
                    }),
                    event,
                );

                if (Result.isOk(data)) {
                    callback(data.data.payload);
                }
            },
        });
    }

    onPublicParticipantOnline(
        this: this,
        participant: ParticipantInput,
        callback: (participant: ParticipantInput, online: boolean) => void,
    ) {
        return matchParticipantInput(participant, {
            user: (userId) =>
                this.onPublicUserOnline(userId, ({ id, online }) => {
                    callback({ userId: id }, online);
                }),
            company: (userId) =>
                this.onPublicUserOnline(userId, ({ id, online }) => {
                    callback({ companyId: id }, online);
                }),
        });
    }

    inSvelte = singleton(() => {
        return new InSvelte(this);
    });

    private onTopic(
        this: this,
        {
            topic,
            callback,
        }: {
            topic: string;
            callback: callback;
        },
    ): () => void {
        if (!this.browser) {
            return () => {};
        }

        this.initTopicEs(topic);

        const listeners = this.topicListenersMap[topic] ?? [];
        this.topicListenersMap[topic] = [...listeners, callback];

        return () => {
            this.offTopic(topic, callback);
        };
    }

    private offTopic(this: this, topic: string, listener: callback) {
        if (!this.browser) {
            return;
        }

        let listeners = this.topicListenersMap[topic] ?? [];
        listeners = listeners.filter((l) => {
            return l !== listener;
        });

        if (listeners.length === 0) {
            this.topicESMap[topic]?.close();
            delete this.topicESMap[topic];
            delete this.topicListenersMap[topic];
        } else {
            this.topicListenersMap[topic] = listeners;
        }
    }

    disconnect() {
        if (!this.browser) {
            return;
        }

        this.topicListenersMap = {};
        for (let topic in this.topicESMap) {
            this.topicESMap[topic]?.close();
        }
        this.topicESMap = {};
    }
}
